import { QueryKeys } from '@common/queries/common';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { useTranspilerImportURL } from '@common/utils/hooks/params';
import { Dialog, toast } from '@prophecy/ui';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useUser } from 'frontend/core/src/context/user';
import { makePostCall, useRestMutation, XHRResponseType } from 'frontend/core/src/data/util';
import { hostUrl } from 'frontend/core/src/utils/getServerUrl';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMixPanel } from '../../../context/mixpanel/context';
import { TEAM_TABS } from '../types';
import { FormContexts } from './useFormContext';
import useUpdateExecutionMetrics from './useUpdateExecutionMetrics';
import useUpdateInfoAspect from './useUpdateInfoAspect';

const createTeamAPI = (payload: { adminID: string; name: string }): XHRResponseType<{ _id: string }> => {
  return makePostCall(`${hostUrl}/api/auth/createTeam`, payload);
};

const useCreateTeam = (formContexts: FormContexts) => {
  const user = useUser();
  const adminID = user?.user?.id;
  const { track } = useMixPanel();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const globalVariables = useGlobalVariables();
  const isProfessionalEdition = globalVariables.isProfessionalEditionEnabled;

  const { isTranspiler, transpilerImportURl, getCompleteUrl } = useTranspilerImportURL();

  const addTeamMutation = useRestMutation(createTeamAPI, { sentryTag: 'createTeam' });

  const basicForm = formContexts[TEAM_TABS.basic];
  const executionMetricsForm = formContexts[TEAM_TABS.executionMetrics];

  const { updateDescription, isLoading: isDescriptionLoading } = useUpdateInfoAspect(basicForm);
  const { updateExecutionMetrics, isLoading: isExecutionMetricsLoading } =
    useUpdateExecutionMetrics(executionMetricsForm);

  const resetModel = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries([QueryKeys.Team]),
      queryClient.invalidateQueries([QueryKeys.User])
    ]);
  }, [queryClient]);

  const backToTeams = useCallback(() => {
    if (isTranspiler) {
      navigate(transpilerImportURl);
    } else {
      navigate(Private_Routes.User.tab.getUrl({ tab: 'teams' }));
    }
  }, [navigate, isTranspiler, transpilerImportURl]);

  const createTeam = usePersistentCallback(async () => {
    if (isProfessionalEdition) {
      const allowCreation = await Dialog.info({
        'data-test-id': 'remove-member-alert-dialog',
        title: `Create a New Team on Prophecy Pro`,
        size: 'l',
        subTitle: (
          <span>
            Your new team will be created on the Prophecy Pro plan. After confirming, you'll be redirected to the
            payment page to complete your setup.
          </span>
        ),
        okButton: {
          children: 'Create',
          variant: 'primary'
        },
        closeButton: {
          children: 'Cancel',
          variant: 'secondaryGray'
        }
      });

      if (allowCreation !== Dialog.ResponseAction.okButton) {
        return;
      }
    }

    const name = basicForm.getValues('name');
    let id: string | undefined;
    try {
      const resp = await addTeamMutation.mutateAsync({
        name,
        adminID
      });
      id = resp.data?._id;
    } finally {
    }

    if (id) {
      track(EntityEvents[Entity.Team].metadata.teamCreateSuccess, { id, adminId: adminID, name });
      await Promise.all([updateDescription(id), updateExecutionMetrics(id)]);
      resetModel();
      if (isProfessionalEdition) {
        // to refetch fabrics list
        user.refetch();
        navigate(Private_Routes.Settings.tab.getUrl({ tab: 'usage&Billing' }, { teamId: id }));
      } else {
        toast.success({ content: 'Team created successfully.' });
        if (isTranspiler) {
          navigate(getCompleteUrl(id, 'teamId'));
        } else {
          backToTeams();
        }
      }
    } else {
      track(EntityEvents[Entity.Team].metadata.teamCreationFailed, { adminId: adminID, name });
      toast.error({ content: Entity.Team + ' creation failed.' });
    }
  });

  return {
    backToTeams,
    createTeam,
    isLoading: addTeamMutation.isLoading || isDescriptionLoading || isExecutionMetricsLoading
  };
};

export default useCreateTeam;
